import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ApprovalRow } from '@/components/ApprovalGrid';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { Timesheet } from '@/types/timesheet';

interface UseApprovalSelectionParams {
  filteredTimesheets: Timesheet[];
  timesheetAnomalies: Map<number, AnomalyDetectionResult>;
}

export interface UseApprovalSelectionReturn {
  selectedIds: Set<number>;
  selectedRows: ApprovalRow[];
  clearSelection: boolean;
  setClearSelection: Dispatch<SetStateAction<boolean>>;
  handleSelectionChange: (rows: ApprovalRow[]) => void;
  selectedTimesheets: Timesheet[];
  selectedWithAnomalies: Timesheet[];
}

/**
 * Owns the grid selection state driven by DataTableEnhanced.
 */
export function useApprovalSelection({
  filteredTimesheets,
  timesheetAnomalies,
}: UseApprovalSelectionParams): UseApprovalSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedRows, setSelectedRows] = useState<ApprovalRow[]>([]);
  const [clearSelection, setClearSelection] = useState(false);

  // When filters change and selected rows are no longer visible, clear stale selection
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visibleIds = new Set(filteredTimesheets.map(ts => ts.id));
    const hasStale = Array.from(selectedIds).some(id => !visibleIds.has(id));
    if (hasStale) {
      setClearSelection(true);
    }
  }, [filteredTimesheets]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection — driven by DataTableEnhanced via onSelectionChange ──────────
  const handleSelectionChange = useCallback((rows: ApprovalRow[]) => {
    setSelectedRows(rows);
    setSelectedIds(new Set(rows.map(r => r.timesheet.id)));
    // Reset the clearSelection trigger after it fires so it can fire again next time
    setClearSelection(false);
  }, []);

  // Get selected timesheets for confirmation dialog — derived from TanStack selection
  const selectedTimesheets = useMemo(() => {
    return selectedRows.map(r => r.timesheet);
  }, [selectedRows]);

  const selectedWithAnomalies = useMemo(() => {
    return selectedTimesheets.filter(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      return anomalyResult?.hasAnomalies;
    });
  }, [selectedTimesheets, timesheetAnomalies]);

  return {
    selectedIds,
    selectedRows,
    clearSelection,
    setClearSelection,
    handleSelectionChange,
    selectedTimesheets,
    selectedWithAnomalies,
  };
}
